import { useEffect, useState } from 'react'
import type { FeedItem } from '../lib/feed'
import { signedPhotoUrl } from '../lib/photos'
import { relativeTime, loadPostOriginal } from '../lib/feed'
import { TranslatedLine, useOriginal } from './TranslatedLine'
import { ReactionBar } from './ReactionBar'
import { PhotoGrid } from './PhotoGrid'
import { useHubLang } from '../lib/i18n'
import { shownIn, HOME } from '../lib/hubCopy'

/**
 * One post in the company feed. Presentational — it takes a row and renders it.
 *
 * ── WHAT THIS CARD LOST WHEN THE FEED BECAME REAL, ON PURPOSE ───────────────
 *
 * The placeholder version showed a view count, a row of reaction pills and a
 * comment count. Views never will exist (rule 8 — aggregate-only, no per-person
 * read model).
 *
 * REACTIONS CAME BACK on 31 Aug, and they are real — migration 018, counts by
 * kind from a security-definer aggregate plus this viewer's own. What did NOT
 * come back is reactor names: the read policy withholds them and no field here
 * carries them. See ReactionBar.
 *
 * COMMENTS CAME BACK on 12 Sep (056). This comment used to say a zero must not
 * be rendered, because "0 comments" claims nobody commented while the truth was
 * that commenting did not exist. That was right, and its subject is gone: the
 * mechanism is 035's, the same one Wiki One has used since 8 Sep, and the count
 * now comes from feed_page. A zero is a fact about the post.
 *
 * ── THE THREAD IS CLOSED UNTIL ASKED FOR, AND MOUNTS ONLY THEN ──────────────
 *
 * Thirty open threads is thirty comments_for() calls on a page that already
 * makes three. More to the point, a feed where every post is a wall of replies
 * is not a feed. So the count is always visible and the conversation is one
 * press away — and because the node is only RENDERED when open, the fetch does
 * not happen until somebody wants it.
 *
 * ── THE THREE BADGES, WHICH ARE NOT DECORATION ──────────────────────────────
 *
 * Each one exists because the alternative is a silent lie:
 *
 *   fallback   The viewer asked for es-CL and got en-CA, because no Spanish
 *              variant exists. Without the badge this reads as "the company
 *              posts in English", and nobody ever files the translation.
 *   edited     content_variant stamps edited_at by trigger when a published
 *              variant changes. 011 was explicit that corrections are visible,
 *              never silent — a post that quietly changes after people read it
 *              is how a comms channel loses its audience.
 *   moderation 'flagged' and 'failed_open' are distinct from 'passed' and both
 *              can be published. failed_open means the FED-10 check errored and
 *              the post went out unchecked; a reader deserves to know which of
 *              those they are looking at.
 *
 * ── AND ONE LINE THAT IS NOT A BADGE: "Translated by One" (111) ────────────
 *
 * translation_status='machine' means One wrote these words, from the author's.
 * Teal and the mark in a ring is how this hub says A MACHINE WROTE THIS, so the
 * line wears both — and it sits ABOVE the title, before anybody reads a word,
 * rather than in the footer with the fallback flag. "Show original" fetches the
 * author's own text on the one press that needs it (post_original), so thirty
 * cards make no extra calls. The reactions, comments and author stay the
 * post's: only the words swap.
 */
export function FeedPost({
  post,
  locale,
  moderationBar,
  thread,
  onEdit,
  initialThreadOpen = false,
  openPhotoPath = null,
  extra,
}: {
  /**
   * 151. Drawn under the photos — the winners of a photo contest the post
   * announced (ContestResults). A node from the route, for the same reason as
   * `thread`: this card stays presentational and knows nothing of contests.
   */
  extra?: React.ReactNode
  post: FeedItem
  locale: string
  /**
   * The comment thread, passed in for the same reason moderationBar is: this
   * component stays presentational and knows nothing about the session. Thread
   * needs a `Me` to decide who may moderate or mark official, and FeedPost is
   * rendered in tests with no session at all.
   *
   * Omitted entirely (rather than passed and hidden) on any surface that should
   * not offer commenting.
   *
   * ── IT IS A FUNCTION NOW, NOT A NODE (12 Sep) ─────────────────────────────
   *
   * The comment bar below needs to tell the thread whether the reader arrived
   * to WRITE or to READ, and a node built by the caller cannot be told anything
   * after the fact. A function taking `{ autoFocus }` keeps the caller owning
   * the session and this card owning the interaction — the alternative was
   * moving the focus decision up to Home, which would mean the route knowing
   * which of a card's two buttons had been pressed.
   */
  thread?: (opts: { autoFocus: boolean }) => React.ReactNode
  /**
   * Moderator controls, when moderator mode is armed. Passed in as a node
   * rather than built here so this component stays presentational and knows
   * nothing about permissions — the decision of who may act on a post is not
   * one a card should be making, and FeedPost is rendered in tests with no
   * session at all.
   */
  moderationBar?: React.ReactNode
  /**
   * 115 (FED-39). Edit YOUR OWN live post. Passed by the route only when the
   * reader wrote it — this card knows nothing about the session, same as the
   * two props above — and the server is what decides (edit_checked_post is
   * author-only). Omitted means no button, never a disabled one.
   */
  onEdit?: () => void
  /**
   * 125 (HLP-23). The #/item/:id permalink opens the post to its conversation,
   * because a bell row about a comment is a row about the thread. The feed
   * never passes it: thirty open threads is thirty comments_for() calls.
   */
  initialThreadOpen?: boolean
  /** 143 (FED-49). Open the photo viewer on this storage path. */
  openPhotoPath?: string | null
}) {
  const lang = useHubLang()
  const h = HOME[lang]
  /* 300. A byline post's heading is the byline, its avatar the byline's mark
     or initials, and the person who posted it is the small line under it —
     the same for every reader (David, 19 Oct). The author is never hidden. */
  const byline = post.byline
  const initials = initialsOf(byline ? byline.name : post.authorName)
  const [showThread, setShowThread] = useState(initialThreadOpen)
  // Which of the two bar buttons opened it. Only read on the mount the press
  // causes — see the `thread` prop and Thread's own autoFocus note.
  const [openedToWrite, setOpenedToWrite] = useState(false)
  // 111. The author's own words, fetched on first press and then kept. The
  // state and the line are TranslatedLine's (112), shared with Wiki One.
  const orig = useOriginal(post.translationStatus === 'machine', () => loadPostOriginal(post.contentId))
  const showingOriginal = orig.showingOriginal && orig.original !== null
  const original = orig.original
  const title = showingOriginal ? original!.title : post.title
  const body = showingOriginal ? original!.body : post.body

  return (
    <article className={`post${post.hiddenAt ? ' post--hidden' : ''}`}>
      <header className="post__head">
        {byline?.mark ? (
          <BylineMark path={byline.mark} initials={initials} />
        ) : (
          <span className={`avatar${byline ? ' avatar--onbrand' : ''}`} aria-hidden="true">
            {initials}
          </span>
        )}
        <div className="post__meta">
          <p className="post__author">{byline ? byline.name : (post.authorName ?? h.authorUnavailable)}</p>
          {byline && (
            <p className="post__postedby">{h.postedBy(post.authorName ?? h.authorUnavailable)}</p>
          )}
          <p className="post__when">
            <time dateTime={post.publishedAt}>
              {relativeTime(post.publishedAt, locale)}
            </time>
            {post.editedAt && <> · {h.edited}</>}
            {onEdit && (
              <>
                {' · '}
                <button type="button" className="post__edit" onClick={onEdit}>
                  {h.editThisPost}
                </button>
              </>
            )}
          </p>
        </div>

        <div className="post__tags">
          {/* No audience rows means the whole tenant, which is the common case
              and worth stating rather than leaving blank — a blank space does
              not distinguish "everyone" from "targeting is broken". */}
          {post.audience.length === 0 ? (
            <span className="tag tag--internal">{h.everyone}</span>
          ) : (
            post.audience.map((a) => (
              <span key={a} className="tag tag--employee">
                {a}
              </span>
            ))
          )}
        </div>
      </header>

      <TranslatedLine state={orig} />

      {title && <h3 className="post__title">{title}</h3>}

      {/* An empty body is a real, intended value: a photo with no words is the
          commonest post on this hub, and create_post accepts it. Rendering an
          empty <p> would leave a gap that reads as a failed load. */}
      {body && <p className="post__excerpt" lang={showingOriginal ? original!.locale : undefined}>{body}</p>}

      <PhotoGrid attachments={post.attachments} openPath={openPhotoPath} />
      {extra}

      {post.tags.length > 0 && (
        <ul className="posttags">
          {post.tags.map((t) => (
            /* Not links yet. A tag that looks clickable and is not is worse
               than one that does not — browsing by tag is FED-18. */
            <li key={t.slug} className="tagchip">
              #{t.label}
            </li>
          ))}
        </ul>
      )}

      {/* Keyed on the post id so switching audience filters — which unmounts
          and remounts these — never carries one post's optimistic reaction
          state onto another's card. */}
      <ReactionBar
        key={post.contentId}
        target={{ contentId: post.contentId }}
        counts={post.reactions}
        mine={post.myReaction}
      />

      {(post.isFallback || post.moderationStatus === 'failed_open') && (
        <footer className="post__foot">
          <ul className="postflags">
            {post.isFallback && (
              <li className="flag" title={h.noVariant(post.requestedLocale)}>
                {shownIn(lang, post.variantLocale)}
              </li>
            )}
            {post.moderationStatus === 'failed_open' && (
              <li className="flag flag--warn" title={h.uncheckedTitle}>
                {h.publishedUnchecked}
              </li>
            )}
          </ul>
        </footer>
      )}

      {thread && (
        <div className="post__comments">
          {/* THE BAR IS ONE CONTROL THAT LOOKS LIKE TWO, AND THAT IS THE POINT.

              David, 12 Sep: "show a small comment bar at the bottom of the feed
              post and below the replies." This is the first of those; the
              second is the compose form inside <Thread>, which moved below the
              list in the same change.

              A real textarea here would be the SECOND compose implementation
              for one act — two places to keep the character limit, the
              disabled-while-empty rule and the error path correct, which is
              what 046 removed from the invite form when a checkbox and a submit
              button were two controls for one decision. So the left half is a
              BUTTON dressed as a field: pressing it opens the thread and puts
              the caret in the real box (`autoFocus`), and there is exactly one
              textarea on the card.

              ── THE READ CONTROL MOVED UNDER THE BAR, AND IT IS BLUE (David and
              Chandra, 25 Sep) ──
              It used to sit on the RIGHT of the bar in muted caption grey,
              reading "2 comments", and Chandra could not find the comments at
              first: grey text at the end of a field reads as a label, not as a
              thing you press. It is now its own line under the bar, in the link
              colour (blue is the brand and says "interactive" — the teal/AI
              rule), and it names the act: "Show 2 comments" / "Hide comments".
              A zero is NOT a control — there is nothing to show — so it stays a
              plain muted fact, still in words and never "0 comments".
              Still two controls and ONE textarea: the reader who wants to READ
              and the one who wants to WRITE each get one press. Showing the
              first two comments without a press was considered and deferred by
              David the same day ("just move the control") — it needs a preview
              on feed_page, FED-35's unbuilt clause. */}
          <div className="commentbar">
            <button
              type="button"
              className="commentbar__field"
              aria-expanded={showThread}
              onClick={() => {
                setOpenedToWrite(true)
                setShowThread(true)
              }}
            >
              {h.addComment}
            </button>
          </div>

          {post.commentCount === 0 ? (
            <p className="commentbar__none">{h.noComments}</p>
          ) : (
            <button
              type="button"
              className="commentbar__count"
              aria-expanded={showThread}
              onClick={() => {
                setOpenedToWrite(false)
                setShowThread((v) => !v)
              }}
            >
              {/* The count is a plain fact from the database. "comment"
                  singular at 1: a card that says "1 comments" is the kind of
                  thing people stop trusting. */}
              {showThread ? h.hideComments : h.showComments(post.commentCount)}
            </button>
          )}

          {/* Mounted only while open — that is what keeps the fetch lazy. The
              count above does NOT update when somebody comments here, because
              it came from feed_page and the thread is not going to write a
              second copy of it: re-reading the feed on every reply would move
              the cards under the reader's thumb. It is right again on the next
              feed load, and a number that lags is better than a page that
              jumps. */}
          {showThread && thread({ autoFocus: openedToWrite })}
        </div>
      )}

      {moderationBar}
    </article>
  )
}

/** 300. A byline's mark, signed from post-media; its initials until then. */
function BylineMark({ path, initials }: { path: string; initials: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    signedPhotoUrl(path)
      .then((u) => live && setUrl(u))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [path])
  return url ? (
    <img className="avatar avatar--mark" src={url} alt="" aria-hidden="true" />
  ) : (
    <span className="avatar avatar--onbrand" aria-hidden="true">
      {initials}
    </span>
  )
}

/**
 * Initials from a display name, or '?' when the author is unreadable.
 *
 * Array.from rather than [0]: display_name comes from HR and is not ASCII, and
 * indexing a string splits an astral character in half.
 */
function initialsOf(displayName: string | null): string {
  if (!displayName) return '?'
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = Array.from(parts[0])[0] ?? ''
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1])[0] ?? '') : ''
  return (first + last).toUpperCase()
}
